import clsx from 'clsx';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { isElement } from 'react-is';

import { _useAriaLabelingProps as useAriaLabelingProps } from '@dynatrace/strato-components/core';

import { useValueNavigation } from './hooks/useValueNavigation.js';
import type { TextSpinButtonProps, TextSpinButtonRef } from './types.js';
import { useSelection } from '../hooks/useSelection.js';
import { spinButtonCSS } from '../SpinButton.sty.js';

/**
 * Allows to check if a ReactNode is a TextSpinButton element.
 * @internal
 */
export function isTextSpinButton(
  node: ReactNode,
): node is ReactElement<TextSpinButtonProps> {
  return (
    node !== undefined &&
    node !== null &&
    isElement(node) &&
    node.type === TextSpinButton
  );
}

/**
 * Use the _TextSpinButton component to pick a text from a list of given text values.
 * @internal
 */
export const TextSpinButton = /* @__PURE__ */ forwardRef<
  TextSpinButtonRef,
  TextSpinButtonProps
>((props, forwardedRef) => {
  const {
    placeholder,
    disabled = false,
    readOnly = false,
    options,
    required = false,
    hasError,
    className: consumerClassName,
    style: consumerStyle,
    onKeyDownHandler: propOnKeyDownHandler,
    'data-testid': dataTestId,
    onChange,
  }: TextSpinButtonProps = props;

  const ariaProps = useAriaLabelingProps(props);
  const rootRef = useRef<HTMLSpanElement>(null);
  const { getSelection, setSelection, selectAll, deselectAll } =
    useSelection(rootRef);

  const [error, setError] = useState(false);

  const { value, onKeyDownHandler, searchValue, clearValue } =
    useValueNavigation(props);

  // provide root element reference and utility
  useImperativeHandle(
    forwardedRef,
    (): TextSpinButtonRef => {
      return {
        type: 'text' as const,
        element: rootRef,
        setSelection,
        getSelection,
        selectAll,
        deselectAll,
        searchValue,
        clearValue,
        onChange,
      };
    },
    [
      clearValue,
      deselectAll,
      getSelection,
      onChange,
      searchValue,
      selectAll,
      setSelection,
    ],
  );

  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }
    rootRef.current.textContent = value ?? '';
    document.activeElement === rootRef.current && selectAll();
    // no need to trigger on selectAll update, only trigger on value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // set error state on value change
  useEffect(() => {
    if (required && value === null) {
      setError(true);
    } else {
      setError(value !== null && !options.includes(value));
    }
  }, [required, value, options]);

  return (
    <span
      data-testid={dataTestId}
      contentEditable={
        // contentEditable when focused and non-editable when blurred, allowing text selection.
        !disabled && !readOnly && (isEditable || value === null)
          ? true
          : undefined
      }
      ref={rootRef}
      tabIndex={disabled ? undefined : 0}
      role="spinbutton"
      spellCheck={false}
      className={clsx(
        consumerClassName,
        spinButtonCSS({ disabled, error: hasError ?? error }),
      )}
      style={
        {
          '--data-placeholder': `"${placeholder}"`,
          ...consumerStyle,
        } as CSSProperties
      }
      aria-disabled={disabled}
      {...ariaProps}
      aria-label={ariaProps['aria-label'] ?? placeholder}
      onKeyDown={propOnKeyDownHandler ?? onKeyDownHandler}
      onFocus={(e) => {
        if (disabled) {
          e.preventDefault();
        } else {
          setIsEditable(true);
          selectAll();
          rootRef.current?.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
          });
        }
      }}
      onBlur={() => {
        deselectAll();
        setIsEditable(false);
      }}
      onBeforeInput={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
    />
  );
});

(
  TextSpinButton as typeof TextSpinButton & { displayName: string }
).displayName = '_TextSpinButton';
